import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldAlert, RefreshCw, LogOut, Loader2, CheckCircle2, XCircle } from 'lucide-react';

import { supabase } from '@/lib/supabase';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';

interface WhoAmI {
  email?: string | null;
  accountType?: string | null;
  isAdmin?: boolean;
  status?: string | null;
  allowlisted?: boolean;
  profileExists?: boolean;
}

/**
 * Shown instead of a bare "restricted" sentence when a signed-in account opens
 * /admin without administrator rights.
 *
 * It answers, in the product, the three questions that otherwise require a
 * support ticket: which account am I signed in as, what is my role, and is the
 * admin flag set? The values come from the server (`admin-set-role` /
 * action=whoami) so they reflect the database, not a stale browser session.
 */
const AdminAccessDenied: React.FC = () => {
  const { lang, path } = useI18n();
  const { profile, refreshProfile, signOut } = useAuth();
  const es = lang === 'es';

  const [info, setInfo] = useState<WhoAmI | null>(null);
  const [checking, setChecking] = useState(true);

  const check = async () => {
    setChecking(true);
    const { data } = await supabase.functions.invoke('admin-set-role', { body: { action: 'whoami' } });
    if (data && !data.error) setInfo(data as WhoAmI);
    await refreshProfile();
    setChecking(false);
  };

  useEffect(() => {
    check();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const email = info?.email || profile?.email || '—';
  const role = info?.accountType || profile?.user_type || '—';
  const isAdmin = info?.isAdmin ?? !!profile?.is_admin;

  const Row = ({ label, value, good }: { label: string; value: string; good?: boolean }) => (
    <div className="flex items-center justify-between gap-3 border-b border-slate-100 py-3 last:border-0">
      <span className="text-xs font-bold uppercase tracking-wide text-slate-400">{label}</span>
      <span className="inline-flex items-center gap-1.5 text-sm font-bold text-slate-800">
        {good === true && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
        {good === false && <XCircle className="h-4 w-4 text-red-500" />}
        {value}
      </span>
    </div>
  );

  return (
    <div className="mx-auto max-w-xl px-4 py-16">
      <div className="rounded-2xl border border-slate-200 bg-white p-6 text-center">
        <ShieldAlert className="mx-auto h-10 w-10 text-amber-500" />
        <h1 className="mt-4 text-xl font-extrabold text-slate-900">
          {es ? 'Esta área es solo para administradores' : 'This area is restricted to platform administrators'}
        </h1>
        <p className="mt-2 text-sm text-slate-500">
          {es
            ? 'Su sesión actual no tiene el permiso de administrador. Estos son los datos exactos de su cuenta.'
            : 'The account you are signed in with does not carry the administrator permission. Here is exactly what your account looks like.'}
        </p>

        <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 px-4 text-left">
          <Row label={es ? 'Cuenta' : 'Signed in as'} value={email} />
          <Row label={es ? 'Tipo de cuenta' : 'Account type'} value={String(role)} />
          <Row
            label={es ? 'Permiso de administrador' : 'Administrator flag'}
            value={isAdmin ? (es ? 'Activo' : 'Enabled') : es ? 'No asignado' : 'Not set'}
            good={isAdmin}
          />
          {info?.allowlisted && (
            <Row
              label={es ? 'Pre-autorizado' : 'Pre-authorised'}
              value={es ? 'Sí' : 'Yes'}
              good
            />
          )}
        </div>

        {isAdmin ? (
          <p className="mt-4 rounded-xl bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700">
            {es
              ? 'Su permiso ya está activo. Recargue esta página para entrar.'
              : 'Your permission is now active. Reload this page to enter.'}
          </p>
        ) : (
          <p className="mt-4 text-xs text-slate-500">
            {es
              ? 'Si tiene otra cuenta de propietario, salga e ingrese con ese correo. El permiso está ligado al correo, no al navegador.'
              : 'If you own another account, sign out and sign in with that email instead. The permission is tied to the email address, not to this browser.'}
          </p>
        )}

        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={isAdmin ? () => window.location.reload() : check}
            className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-5 py-3 text-sm font-bold text-white hover:bg-slate-800"
          >
            {checking ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            {es ? 'Volver a comprobar el acceso' : 'Re-check my access'}
          </button>
          <button
            onClick={() => signOut()}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-5 py-3 text-sm font-bold text-slate-700 hover:bg-slate-50"
          >
            <LogOut className="h-4 w-4" /> {es ? 'Cambiar de cuenta' : 'Switch account'}
          </button>
          <Link
            to={path('/dashboard')}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-5 py-3 text-sm font-bold text-slate-700 hover:bg-slate-50"
          >
            {es ? 'Ir al panel' : 'Go to dashboard'}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AdminAccessDenied;
